import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import ApiAdapterError from "../../../utils/ApiAdapterError";
import { getServerSettings } from "../api";
import { useDiscordServerAccessStatus } from "./useDiscordServerAccessStatus";

interface Props {
  serverId?: string;
}

export interface UseDiscordServerSettingsData {
  profile: {
    dateFormat: string;
    timezone: string;
  };
}

export const useDiscordServerSettings = ({ serverId }: Props) => {
  const [hasErrored, setHasErrored] = useState(false);
  const { data: accessStatus, isFetching } = useDiscordServerAccessStatus({ serverId });

  const { data, error, status, refetch } = useQuery<UseDiscordServerSettingsData, ApiAdapterError>(
    {
      queryKey: ["server-settings", serverId],
      queryFn: async () => {
        if (!serverId) {
          throw new Error("Server ID is required when fetching discord server settings");
        }

        const response = await getServerSettings({ serverId });

        return {
          profile: {
            dateFormat: response.result.profile.dateFormat,
            timezone: response.result.profile.timezone,
          },
        };
      },
      enabled: accessStatus?.result.authorized && !hasErrored && !!serverId,
      retry: (failureCount: number, e: ApiAdapterError) => {
        if (e.statusCode === 404) {
          return false;
        }

        return failureCount < 3;
      },
    },
  );

  useEffect(() => {
    if (error) {
      setHasErrored(true);
    }
  }, [error]);

  return {
    data,
    error,
    status,
    isFetching,
    refetch,
  };
};
